import { z } from 'zod'
import { WAVE_FILE_TYPES } from '../../../lib/parsers/types'

/**
 * Zod schemas for Template V3 (current version)
 * 
 * Single source of truth for template validation
 * TypeScript types are inferred from these schemas using z.infer<>
 */

// Core axis configuration schema (V3 - range-centric)
const YAxisConfigSchema = z.object({
  label: z.string().optional(),
  unit: z.string().optional(),
  range: z.object({
    min: z.number(),
    max: z.number()
  }).optional(), // undefined = auto-range, defined = manual range
  color: z.string().optional(),
  showGridLines: z.boolean().optional()
})

const XAxisConfigSchema = z.object({
  label: z.string().optional(),
  unit: z.string().optional(), // TODO: Make this more specific with XAxisUnit enum
  range: z.object({
    min: z.number(),
    max: z.number()
  }).optional(), // undefined = auto-range, defined = manual range
  showGridLines: z.boolean().optional(),
  tickInterval: z.number().optional()
})

// Curve reference schema - matches application's StyledCurveReference format
const CurveReferenceSchema = z.object({
  reference: z.object({
    fileType: z.enum(WAVE_FILE_TYPES),
    curveName: z.string()
  }),
  styling: z.object({
    color: z.string().optional(),
    strokeThickness: z.number().optional(),
    lineStyle: z.enum(['solid', 'dashed', 'dotted', 'dashdot']).optional(),
    yAxisId: z.enum(['left', 'right', 'left2', 'right2']).optional(),
    visible: z.boolean().optional(),
    opacity: z.number().optional(),
    interpolation: z.enum(['linear', 'stepBefore', 'stepAfter', 'stepMiddle', 'spline', 'none']).optional(),
    showDataPoints: z.boolean().optional(),
    pointStyle: z.enum(['none', 'circle', 'square', 'triangle', 'cross']).optional(),
    pointSize: z.number().optional(),
    displayName: z.string().max(100).optional()
  }).optional()
})

// Chart configuration schema
const ChartConfigSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  curveReferences: z.array(CurveReferenceSchema),
  heightPreset: z.enum(['small', 'medium', 'large', 'xl']).optional(),
  showGrid: z.boolean().optional(),
  showCrosshairs: z.boolean().optional(),
  showTooltips: z.boolean().optional(),
  backgroundColor: z.string().optional(),
  legendCorner: z.enum(['tl','tr','bl','br']).optional(),
  legendMode: z.enum(['overlay','bottom','none']).optional(),
  yAxes: z.object({
    left: YAxisConfigSchema.optional(),
    right: YAxisConfigSchema.optional(),
    left2: YAxisConfigSchema.optional(),
    right2: YAxisConfigSchema.optional()
  }).optional()
})

// V6 Template schema (current version)
export const TemplateSchemaV6 = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  chartConfigs: z.array(ChartConfigSchema),
  version: z.literal(6), // Current version
  accessLevel: z.enum(['private', 'org', 'public']),
  organizationId: z.string().optional(),
  userId: z.string(),
  status: z.enum(['draft', 'active', 'archived', 'deprecated']),
  createdAt: z.date(),
  updatedAt: z.date(),
  lastUsedAt: z.date().optional()
})

// V3 Template schema (legacy support for tests/fixtures)
export const TemplateSchemaV3 = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  chartConfigs: z.array(ChartConfigSchema),
  version: z.literal(3),
  accessLevel: z.enum(['private', 'org', 'public']),
  organizationId: z.string().optional(),
  userId: z.string(),
  status: z.enum(['draft', 'active', 'archived', 'deprecated']),
  createdAt: z.date(),
  updatedAt: z.date(),
  lastUsedAt: z.date().optional()
})

// Back-compat export for tests that validate V3 explicitly
export const TemplateSchema = TemplateSchemaV3

// Create/Update request schemas
export const CreateTemplateRequestSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  chartConfigs: z.array(ChartConfigSchema),
  accessLevel: z.enum(['private', 'org', 'public']),
  organizationId: z.string().optional()
})

export const UpdateTemplateRequestSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  chartConfigs: z.array(ChartConfigSchema).optional(),
  accessLevel: z.enum(['private', 'org', 'public']).optional(),
  organizationId: z.string().optional(),
  status: z.enum(['draft', 'active', 'archived', 'deprecated']).optional()
})

// Search and response schemas
export const TemplateSearchFiltersSchema = z.object({
  query: z.string().optional(),
  accessLevels: z.array(z.enum(['private', 'org', 'public'])).optional(),
  statuses: z.array(z.enum(['draft', 'active', 'archived', 'deprecated'])).optional(),
  organizationId: z.string().optional(),
  userId: z.string().optional(),
  userAccessibleOnly: z.boolean().optional(),
  sortBy: z.enum(['name', 'createdAt', 'updatedAt', 'lastUsedAt']).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional()
})

export const TemplateSearchResponseSchema = z.object({
  templates: z.array(TemplateSchemaV6),
  totalCount: z.number()
})

// Infer TypeScript types from Zod schemas (single source of truth)
// Current template type is V5
export type Template = z.infer<typeof TemplateSchemaV6>
export type ChartConfig = z.infer<typeof ChartConfigSchema>
export type YAxisConfig = z.infer<typeof YAxisConfigSchema>
export type CreateTemplateRequest = z.infer<typeof CreateTemplateRequestSchema>
export type UpdateTemplateRequest = z.infer<typeof UpdateTemplateRequestSchema>
export type TemplateSearchFilters = z.infer<typeof TemplateSearchFiltersSchema>
export type TemplateSearchResponse = z.infer<typeof TemplateSearchResponseSchema>

// Type aliases for compatibility
export type TemplateAccessLevel = 'private' | 'org' | 'public'
export type TemplateStatus = 'draft' | 'active' | 'archived' | 'deprecated'
export type LegacyTemplateV3 = z.infer<typeof TemplateSchemaV3>
export type ChartTemplate = Template // Current alias

// Additional types for template system
export interface TemplateMigration {
  fromVersion: number
  toVersion: number
  migrate: (template: unknown) => unknown
  description: string
}

export interface ActiveTemplateState {
  activeTemplateId?: string
  hasUnsavedChanges: boolean
  appliedAt?: Date
  originalTemplateData?: Template
}

export interface WorkspaceTemplateState {
  activeTemplate: ActiveTemplateState
  recentTemplates: string[]
}

// Export schemas for validation
export { 
  YAxisConfigSchema,
  XAxisConfigSchema,
  ChartConfigSchema,
  CurveReferenceSchema
} 
